import type {
  ColDef,
  ICellRendererParams,
  ITooltipParams,
  TextMatcherParams,
  ValueFormatterParams
} from "ag-grid-enterprise";
import { ForecastMetaDataType } from "../../../../hooks/useFetchForecastMetaData";
import { yearColId } from "../../../../savedViews/resolveViewRows";
import { isAlgorithmicProducer } from "../../../../utils/algorithmicProducer";
import { getDisplayLabel } from "../../../../utils/summary/summaryPageHelpers";
import { metricComparator } from "../../utils/columnUtils";
import { CalendarType } from "../columnFactory";
import { ProducerCell, ProducerTooltip } from "./ProducerCell";
import { formatProducer } from "./producerLabel";

/**
 * Matches a metric on its displayed label or its stored id, so "GS I" and
 * "GS1" both find the row. AG Grid's own matcher tests one string; a negative
 * option has to invert the pair, not each half, or "not GS1" would keep the
 * row on the strength of its label.
 */
function metricTextMatcher(
  params: TextMatcherParams,
  metaData: ForecastMetaDataType
): boolean {
  const { filterOption, filterText, value, data } = params;
  if (filterText == null) {
    return false;
  }
  // `value` is the id, lower-cased by AG Grid alongside `filterText`.
  const candidates = [
    String(value ?? ""),
    getDisplayLabel(String(data?.metric_name ?? ""), metaData).toLowerCase()
  ];
  switch (filterOption) {
    case "contains":
      return candidates.some((it) => it.includes(filterText));
    case "notContains":
      return !candidates.some((it) => it.includes(filterText));
    case "equals":
      return candidates.some((it) => it === filterText);
    case "notEqual":
      return !candidates.some((it) => it === filterText);
    case "startsWith":
      return candidates.some((it) => it.startsWith(filterText));
    case "endsWith":
      return candidates.some((it) => it.endsWith(filterText));
    default:
      return false;
  }
}

/**
 * The producer column. Only algorithmic rows render the badge and carry the
 * load-metadata tooltip; every other row keeps the formatter alone.
 */
function producerColumn(): ColDef {
  return {
    field: "producer_type",
    headerName: "Producer",
    pinned: "left",
    minWidth: 150,
    type: "string",
    cellStyle: {
      textTransform: "uppercase"
    },
    valueFormatter: (params: ValueFormatterParams) =>
      formatProducer(params.value),
    // A React renderer only where the badge is; every other row keeps the
    // formatter alone.
    cellRendererSelector: (params: ICellRendererParams) =>
      isAlgorithmicProducer(params.value)
        ? { component: ProducerCell }
        : undefined,
    // AG Grid shows a tooltip only when the getter returns a value; the
    // component reads everything it renders off the row.
    tooltipValueGetter: (params: ITooltipParams) =>
      isAlgorithmicProducer(params.value) ? params.value : undefined,
    tooltipComponent: ProducerTooltip,
    filter: "agMultiColumnFilter",
    filterParams: {
      filters: [
        {
          filter: "agTextColumnFilter",
          display: "accordion",
          filterParams: { buttons: ["apply", "reset"] }
        },
        {
          filter: "agSetColumnFilter",
          display: "accordion",
          filterParams: { buttons: ["apply", "reset"] }
        }
      ]
    }
  };
}

export function createColumnsPinnedLeft(
  calendarType: CalendarType,
  metaData: ForecastMetaDataType
): ColDef[] {
  const yearField = yearColId(calendarType);
  return [
    {
      pinned: "left",
      minWidth: 150,
      field: "metric_name",
      headerName: "Metric",
      type: "string",
      comparator: (valueA, valueB) =>
        metricComparator([valueA, valueB], metaData),
      valueFormatter: function (params: ValueFormatterParams) {
        return getDisplayLabel(params.value, metaData);
      },
      tooltipValueGetter: (params: ITooltipParams) =>
        metaData.metricTooltipMapping[params.value] ??
        getDisplayLabel(params.value, metaData),
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            filterParams: {
              buttons: ["apply", "reset"],
              textMatcher: (params: TextMatcherParams) =>
                metricTextMatcher(params, metaData)
            }
          },
          {
            filter: "agSetColumnFilter",
            display: "accordion",
            filterParams: {
              buttons: ["apply", "reset"],
              // Keys from the metadata, not the rows — see
              // ``docs/saved-views-contract.md`` § the metric filter.
              values: Object.keys(metaData.metricDisplayMapping),
              valueFormatter: function (params: ValueFormatterParams) {
                return getDisplayLabel(params.value, metaData);
              }
            }
          }
        ]
      }
    },
    {
      field: yearField,
      headerName: yearField === "year" ? "Year" : "Fiscal Year",
      pinned: "left",
      minWidth: 150,
      type: "string",
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            filterParams: { buttons: ["apply", "reset"] }
          },
          {
            filter: "agSetColumnFilter",
            display: "accordion",
            filterParams: { buttons: ["apply", "reset"] }
          }
        ]
      }
    },
    producerColumn()
  ];
}
